<template>
  <div class="heritage-page">
    <!-- 主要内容区域 -->
    <main style="margin-top: 70px;">
      <div class="dragon-boat-container">
        <!-- 页面头部区块 -->
        <section class="page-header">
          <h1>龙舟设计工坊</h1>
          <p>传承千年龙舟文化，打造现代设计理念</p>
        </section>

        <!-- 虚拟实验室模块 -->
        <section class="virtual-lab">
          <h2>虚拟实验室</h2>
          
          <!-- 龙舟部件选择和图片显示区域 -->
          <div class="lab-workspace">
            <!-- 左侧：龙舟部件选择（竖向排列） -->
            <div class="dragon-parts-sidebar">
              <div 
                v-for="part in dragonParts" 
                :key="part.type"
                class="dragon-part"
                :class="{ active: selectedPart === part.type }"
                @click="selectPart(part.type)"
              >
                <div class="dragon-part-icon">
                  <i :class="part.icon"></i>
                </div>
                <div class="dragon-part-content">
                  <h3>{{ part.name }}</h3>
                  <p>{{ part.description }}</p>
                </div>
              </div>
            </div>

            <!-- 右侧：零件图片显示区域 -->
            <div 
              class="parts-display" 
              :class="{ 'has-image': selectedPart }"
            >
              <div v-if="!selectedPart" class="placeholder-content">
                <div class="placeholder-icon">
                  <i class="fas fa-mouse-pointer"></i>
                </div>
                <div class="placeholder-text">
                  选中龙头时出现的是龙头有关的零件图片，选中龙身，龙尾时也出现对应的零件图片图片。（左侧选中龙头、龙身、龙尾需要做三个跳转页面）
                </div>
              </div>
              
              <!-- 部件图片展示（显示多张图片） -->
              <div v-if="selectedPart" class="parts-images-grid">
                <div 
                  v-for="(image, index) in getPartImages(selectedPart)" 
                  :key="index"
                  class="part-image-item"
                  :class="{ 'selected-image': selectedImage === image }"
                  @click="selectPartImage(image, index)"
                >
                  <img 
                    :src="image" 
                    :alt="`${getPartName(selectedPart)}图片${index + 1}`" 
                    class="parts-image-small"
                    @error="handleImageError"
                  >
                  <div class="image-overlay">
                    <i :class="selectedImage === image ? 'fas fa-check-circle' : 'fas fa-eye'"></i>
                    <span>{{ selectedImage === image ? '已选择' : '选择图片' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 龙舟拼接模块 -->
        <section class="dragon-assembly">
          <h2>龙舟拼接工坊</h2>
          
          <div class="assembly-workspace">
            <!-- 拼接预览区域 -->
            <div class="assembly-preview">
              <div class="assembly-parts">
                <!-- 龙头拼接区域 -->
                <div 
                  class="assembly-part head-part"
                  :class="{ 
                    active: assemblyParts.head, 
                    highlighted: selectedPart === 'head' 
                  }"
                  @click="selectAssemblyPart('head')"
                >
                  <div v-if="!assemblyParts.head" class="assembly-placeholder">
                    <i class="fas fa-dragon"></i>
                    <span>点击选择龙头</span>
                  </div>
                  <img 
                    v-else
                    :src="assemblyParts.head" 
                    alt="选中的龙头" 
                    class="assembly-image"
                    @error="handleImageError"
                  >
                </div>

                <!-- 龙身拼接区域 -->
                <div 
                  class="assembly-part body-part"
                  :class="{ 
                    active: assemblyParts.body, 
                    highlighted: selectedPart === 'body' 
                  }"
                  @click="selectAssemblyPart('body')"
                >
                  <div v-if="!assemblyParts.body" class="assembly-placeholder">
                    <i class="fas fa-ship"></i>
                    <span>点击选择龙身</span>
                  </div>
                  <img 
                    v-else
                    :src="assemblyParts.body" 
                    alt="选中的龙身" 
                    class="assembly-image"
                    @error="handleImageError"
                  >
                </div>

                <!-- 龙尾拼接区域 -->
                <div 
                  class="assembly-part tail-part"
                  :class="{ 
                    active: assemblyParts.tail, 
                    highlighted: selectedPart === 'tail' 
                  }"
                  @click="selectAssemblyPart('tail')"
                >
                  <div v-if="!assemblyParts.tail" class="assembly-placeholder">
                    <i class="fas fa-fish"></i>
                    <span>点击选择龙尾</span>
                  </div>
                  <img 
                    v-else
                    :src="assemblyParts.tail" 
                    alt="选中的龙尾" 
                    class="assembly-image"
                    @error="handleImageError"
                  >
                </div>
              </div>

              <!-- 拼接操作按钮 -->
              <div class="assembly-actions">
                <button 
                  class="assembly-btn reset-btn"
                  @click="resetAssembly"
                >
                  <i class="fas fa-undo"></i>
                  重置拼接
                </button>
                <button 
                  class="assembly-btn complete-btn"
                  :disabled="!isAssemblyComplete"
                  @click="completeAssembly"
                >
                  <i class="fas fa-check"></i>
                  完成拼接
                </button>
              </div>
            </div>


          </div>
        </section>

        <!-- 设计工具模块 -->
        <section class="design-tools">
          <h2>完整的龙舟图片</h2>
          
          <div class="design-workspace">
            <!-- 左侧工具面板 -->
            <div class="tool-panel">
              <h3>颜色选择</h3>
              <div class="color-buttons">
                <button 
                  v-for="color in colorOptions" 
                  :key="color.name"
                  class="color-btn"
                  :class="{ active: selectedColor === color.name }"
                  :style="{ background: color.gradient }"
                  @click="selectColor(color.name)"
                >
                  <i class="fas fa-palette"></i> {{ color.label }}
                </button>
              </div>
            </div>

            <!-- 中间设计画布 -->
            <div 
              class="design-canvas"
              :class="{ active: selectedColor || isAssemblyComplete }"
            >
              <div v-if="!selectedColor && !isAssemblyComplete" class="canvas-placeholder">
                <div class="canvas-placeholder-icon">
                  <i class="fas fa-image"></i>
                </div>
                <h3>点击左侧按钮查看龙舟图片或完成拼接查看作品</h3>
              </div>
              
              <!-- 拼接完成后的龙舟展示 -->
              <div v-if="isAssemblyComplete && !selectedColor" class="assembled-dragon-display">
                <div class="assembled-dragon-header">
                  <h3>🎉 您的拼接作品</h3>
                  <p>恭喜完成龙舟拼接！</p>
                </div>
                <div class="assembled-dragon-parts">
                  <img 
                    :src="assemblyParts.head" 
                    alt="拼接的龙头" 
                    class="assembled-part head-display"
                    @error="handleImageError"
                  >
                  <img 
                    :src="assemblyParts.body" 
                    alt="拼接的龙身" 
                    class="assembled-part body-display"
                    @error="handleImageError"
                  >
                  <img 
                    :src="assemblyParts.tail" 
                    alt="拼接的龙尾" 
                    class="assembled-part tail-display"
                    @error="handleImageError"
                  >
                </div>
                <div class="assembled-dragon-info">
                  <div class="assembly-stats">
                    <div class="stat-item">
                      <i class="fas fa-dragon"></i>
                      <span>龙头: 已拼接</span>
                    </div>
                    <div class="stat-item">
                      <i class="fas fa-ship"></i>
                      <span>龙身: 已拼接</span>
                    </div>
                    <div class="stat-item">
                      <i class="fas fa-fish"></i>
                      <span>龙尾: 已拼接</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 龙舟完整图片 -->
              <img 
                v-show="selectedColor && !isAssemblyComplete"
                :src="dragonBoatImage" 
                alt="完整龙舟" 
                class="canvas-image"
                @error="handleImageError"
              >
            </div>

            <!-- 右侧属性面板 -->
            <div class="property-panel">
              <h3>船速</h3>
              <div class="property-group">
                <label for="boatSpeed">船速值：</label>
                <input 
                  type="range" 
                  id="boatSpeed"
                  v-model="properties.boatSpeed"
                  class="property-input" 
                  min="1" 
                  max="100"
                >
                <input 
                  type="number" 
                  v-model="properties.boatSpeed"
                  class="property-input number-input" 
                  min="1" 
                  max="100"
                >
                <span>km/h</span>
              </div>

              <h3 style="margin-top: 30px;">水的流速</h3>
              <div class="property-group">
                <label for="waterSpeed">流速值：</label>
                <input 
                  type="range" 
                  id="waterSpeed"
                  v-model="properties.waterSpeed"
                  class="property-input" 
                  min="1" 
                  max="100"
                >
                <input 
                  type="number" 
                  v-model="properties.waterSpeed"
                  class="property-input number-input" 
                  min="1" 
                  max="100"
                >
                <span>km/h</span>
              </div>

              <h3 style="margin-top: 30px;">船底弧度</h3>
              <div class="property-group">
                <label for="boatCurvature">弧度系数：</label>
                <input 
                  type="range" 
                  id="boatCurvature"
                  v-model="properties.boatCurvature"
                  class="property-input" 
                  min="0.1" 
                  max="2.0" 
                  step="0.1"
                >
                <input 
                  type="number" 
                  v-model="properties.boatCurvature"
                  class="property-input number-input" 
                  min="0.1" 
                  max="2.0" 
                  step="0.1"
                >
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>

    <!-- 页面底部 -->
    <footer class="main-footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <h4>关于我们</h4>
            <p>致力于保护和传承中华民族非物质文化遗产</p>
          </div>
          <div class="footer-section">
            <h4>快速链接</h4>
            <ul>
              <li><router-link to="/about">认识非遗</router-link></li>
              <li><router-link to="/explore">走近非遗</router-link></li>
              <li><router-link to="/heritage">传习非遗</router-link></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>联系我们</h4>
            <p>邮箱: info@heritage.cn</p>
            <p>电话: 010-12345678</p>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2024 中华非遗. 保留所有权利.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HeritagePage',
  
  data() {
    return {
      /**
       * 选中的龙舟部件
       */
      selectedPart: null,
      
      /**
       * 选中的颜色主题
       */
      selectedColor: null,
      
      /**
       * 龙舟部件数据
       */
      dragonParts: [
        {
          type: 'head',
          name: '龙头',
          description: '龙舟的精神象征，威武神圣的造型设计',
          icon: 'fas fa-dragon',
          images: [
            '/img/tou/1.png',
            '/img/tou/2.jpg',
            '/img/tou/3.png',
            '/img/tou/4.jpg'
          ]
        },
        {
          type: 'body',
          name: '龙身',
          description: '包括内部精细结构，承载力与美观的完美结合',
          icon: 'fas fa-ship',
          images: [
            '/img/body/1.png',
            '/img/body/2.jpg',
            '/img/body/3.png',
            '/img/body/4.jpg'
          ]
        },
        {
          type: 'tail',
          name: '龙尾',
          description: '平衡与推进的关键部位，优雅的收尾设计',
          icon: 'fas fa-fish',
          images: [
            '/img/wei/1.jpg',
            '/img/wei/2.jpg',
            '/img/wei/3.png',
            '/img/wei/4.png'
          ]
        }
      ],
      
      /**
       * 颜色选项数据
       */
      colorOptions: [
        {
          name: 'red',
          label: '红色',
          gradient: 'linear-gradient(145deg, #FC020A, #FC020A)'
        },
        {
          name: 'gold',
          label: '金色',
          gradient: 'linear-gradient(145deg, #ffc107, #e0a800)'
        },
        {
          name: 'blue',
          label: '蓝色',
          gradient: 'linear-gradient(145deg, #007bff, #0056b3)'
        },
        {
          name: 'green',
          label: '绿色',
          gradient: 'linear-gradient(145deg, #28a745, #1e7e34)'
        },
        {
          name: 'purple',
          label: '紫色',
          gradient: 'linear-gradient(145deg, #6f42c1, #5a32a3)'
        },
        {
          name: 'orange',
          label: '橙色',
          gradient: 'linear-gradient(145deg, #fd7e14, #e55100)'
        },
        {
          name: 'pink',
          label: '粉色',
          gradient: 'linear-gradient(145deg, #e83e8c, #d91a72)'
        },
        {
          name: 'cyan',
          label: '青色',
          gradient: 'linear-gradient(145deg, #17a2b8, #138496)'
        }
      ],
      
      /**
       * 龙舟完整图片
       */
      dragonBoatImage: '/img/龙舟结构图/绘制龙舟结构图 (9).png',
      
      /**
       * 属性面板数据
       */
      properties: {
        boatSpeed: 30,
        waterSpeed: 50,
        boatCurvature: 1.0
      },
      
      /**
       * 龙舟拼接模块数据
       */
      assemblyParts: {
        head: null,
        body: null,
        tail: null
      },
      
             /**
        * 当前选中的图片
        */
       selectedImage: null
    }
  },
  
  computed: {
    /**
     * 判断拼接是否完成
     * @returns {boolean} - 是否完成拼接
     */
    isAssemblyComplete() {
      return !!(this.assemblyParts.head && this.assemblyParts.body && this.assemblyParts.tail)
    }
  },
  
  methods: {
    /**
     * 选择龙舟部件
     * @param {string} partType - 部件类型
     */
    selectPart(partType) {
      this.selectedPart = partType
      
      // 记录选择操作
      console.log(`选择了龙舟部件: ${partType}`)
      
      // 可以在这里添加更多的交互逻辑
      this.animatePartSelection()
    },
    
    /**
     * 选择颜色主题
     * @param {string} colorName - 颜色名称
     */
    selectColor(colorName) {
      this.selectedColor = colorName
      
      // 记录颜色选择
      console.log(`选择了颜色主题: ${colorName}`)
      
      // 可以根据颜色调整图片或其他视觉效果
      this.applyColorTheme(colorName)
    },
    
    /**
     * 应用颜色主题
     * @param {string} colorName - 颜色名称
     */
    applyColorTheme(colorName) {
      // 根据选择的颜色应用不同的主题效果
      const themeActions = {
        red: () => console.log('应用红色主题'),
        gold: () => console.log('应用金色主题'),
        blue: () => console.log('应用蓝色主题'),
        green: () => console.log('应用绿色主题'),
        purple: () => console.log('应用紫色主题'),
        orange: () => console.log('应用橙色主题'),
        pink: () => console.log('应用粉色主题'),
        cyan: () => console.log('应用青色主题')
      }
      
      if (themeActions[colorName]) {
        themeActions[colorName]()
      }
    },
    
    /**
     * 部件选择动画效果
     */
    animatePartSelection() {
      // 为选中的部件添加动画效果
      this.$nextTick(() => {
        const activeElement = document.querySelector('.dragon-part.active')
        if (activeElement) {
          activeElement.style.transform = 'scale(1.05)'
          setTimeout(() => {
            activeElement.style.transform = ''
          }, 200)
        }
      })
    },
    
    /**
     * 处理图片加载错误
     * @param {Event} event - 错误事件
     */
    handleImageError(event) {
      console.warn('图片加载失败:', event.target.src)
      event.target.src = '/img/default-heritage.jpg'
    },
    
         /**
      * 获取部件的图片
      * @param {string} partType - 部件类型
      * @returns {Array} - 部件的图片数组
      */
     getPartImages(partType) {
       const part = this.dragonParts.find(part => part.type === partType)
       return part ? part.images : []
     },
    
    /**
     * 获取部件的名称
     * @param {string} partType - 部件类型
     * @returns {string} - 部件的名称
     */
    getPartName(partType) {
      const part = this.dragonParts.find(part => part.type === partType)
      return part ? part.name : ''
    },
    
         /**
      * 选择部件图片
      * @param {string} image - 图片路径
      * @param {number} index - 图片索引
      */
     selectPartImage(image, index) {
       this.selectedImage = image
       console.log(`选择了部件图片: ${image}, 索引: ${index}`)
       
       // 如果有选中的部件，则自动放置图片
       if (this.selectedPart && this.selectedImage) {
         this.assemblyParts[this.selectedPart] = this.selectedImage
         console.log(`自动放置图片到 ${this.selectedPart}`)
       }
     },
     
     /**
      * 选择拼接部件
      * @param {string} partType - 部件类型
      */
     selectAssemblyPart(partType) {
       // 自动选择对应的部件类型
       this.selectedPart = partType
       console.log(`选择了拼接部件: ${partType}`)
       
       // 如果有选中的图片，则放置图片
       if (this.selectedImage) {
         this.assemblyParts[partType] = this.selectedImage
         console.log(`放置图片到 ${partType}`)
       } else {
         // 如果没有选中图片，自动选择该部件的第一张图片
         const partImages = this.getPartImages(partType)
         if (partImages.length > 0) {
           this.assemblyParts[partType] = partImages[0]
           this.selectedImage = partImages[0]
           console.log(`自动选择 ${partType} 的第一张图片`)
         }
       }
     },
     
     /**
      * 重置拼接
      */
     resetAssembly() {
       this.assemblyParts = {
         head: null,
         body: null,
         tail: null
       }
       this.selectedImage = null
       this.selectedPart = null
       // 重置时清除颜色选择，回到初始状态
       this.selectedColor = null
       console.log('重置拼接')
       
       // 显示重置提示
       alert('拼接已重置，请重新选择龙舟部件进行拼接！')
     },
     
     /**
      * 完成拼接
      */
     completeAssembly() {
       if (this.isAssemblyComplete) {
         console.log('完成拼接，龙舟组装完成！')
         
         // 清除颜色选择，优先显示拼接作品
         this.selectedColor = null
         
         // 滚动到完整龙舟图片区域
         this.$nextTick(() => {
           const designSection = document.querySelector('.design-tools')
           if (designSection) {
             designSection.scrollIntoView({ 
               behavior: 'smooth', 
               block: 'start' 
             })
           }
         })
         
         // 显示完成提示
         setTimeout(() => {
           const message = `
🎉 恭喜！龙舟拼接完成！

您成功组装了一艘完整的龙舟：
🐲 龙头：已选择
🚢 龙身：已选择  
🐟 龙尾：已选择

传承千年龙舟文化，您的设计充满了传统与现代的完美融合！

您的拼接作品已在下方"完整的龙舟图片"区域展示。
           `
           alert(message)
         }, 800)
       }
     }
  },
  
  watch: {
    /**
     * 监听属性变化，实时更新设计
     */
    properties: {
      deep: true,
      handler(newProps) {
        console.log('设计属性更新:', newProps)
        // 可以在这里添加实时预览逻辑
      }
    }
  }
}
</script>

<style scoped>
/* 龙舟设计页面专用样式 */
.heritage-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.dragon-boat-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
  background: linear-gradient(135deg, #ff6b6b, #feca57);
  color: white;
  padding: 60px 20px;
  border-radius: 15px;
}

.page-header h1 {
  font-size: 3rem;
  margin-bottom: 15px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-header p {
  font-size: 1.2rem;
  opacity: 0.9;
}

/* 虚拟实验室模块 */
.virtual-lab {
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 2px solid #e74c3c;
}

.virtual-lab h2 {
  color: #e74c3c;
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 30px;
  font-weight: bold;
}

.lab-workspace {
  display: flex;
  gap: 30px;
}

.dragon-parts-sidebar {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.dragon-part {
  background: linear-gradient(145deg, #f8f9fa, #e9ecef);
  border: 3px solid #dc3545;
  border-radius: 15px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 15px;
}

.dragon-part:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(220, 53, 69, 0.3);
  border-color: #c82333;
}

.dragon-part.active {
  background: linear-gradient(145deg, #dc3545, #c82333);
  color: white;
}

.dragon-part-icon {
  font-size: 2.5rem;
  color: #dc3545;
  transition: all 0.3s ease;
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(220, 53, 69, 0.1);
  border-radius: 50%;
}

.dragon-part.active .dragon-part-icon {
  color: white;
  background: rgba(255, 255, 255, 0.2);
}

.dragon-part-content {
  text-align: left;
  flex: 1;
}

.dragon-part h3 {
  font-size: 1.3rem;
  margin-bottom: 8px;
  font-weight: bold;
}

.dragon-part p {
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 0;
  color: #6c757d;
}

.dragon-part.active p {
  color: rgba(255, 255, 255, 0.9);
}

/* 图片网格样式 */
.parts-images-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 600px;
}

.part-image-item {
  position: relative;
  cursor: pointer;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid #dee2e6;
}

.part-image-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  border-color: #007bff;
}

.part-image-item.selected-image {
  border-color: #28a745;
  box-shadow: 0 5px 15px rgba(40, 167, 69, 0.4);
  transform: scale(1.02);
}

.part-image-item.selected-image .image-overlay {
  background: rgba(40, 167, 69, 0.9);
  opacity: 1;
}

.parts-image-small {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 123, 255, 0.8);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.part-image-item:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  font-size: 2rem;
  margin-bottom: 10px;
}

/* 零件图片显示区域 */
.parts-display {
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 2;
}

.parts-display.has-image {
  border-color: #28a745;
  background: #f8fff9;
}

.parts-image {
  max-width: 100%;
  max-height: 350px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.placeholder-text {
  color: #6c757d;
  font-size: 1.2rem;
  margin-bottom: 15px;
  max-width: 600px;
  line-height: 1.5;
}

.placeholder-icon {
  font-size: 4rem;
  color: #dee2e6;
  margin-bottom: 20px;
}

/* 龙舟拼接模块 */
 .dragon-assembly {
   background: #fff;
   border-radius: 15px;
   padding: 30px;
   margin-bottom: 40px;
   box-shadow: 0 10px 30px rgba(0,0,0,0.1);
   border: 2px solid #28a745;
 }

 .dragon-assembly h2 {
   color: #28a745;
   text-align: center;
   font-size: 2.5rem;
   margin-bottom: 30px;
   font-weight: bold;
 }

 .assembly-workspace {
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 20px;
 }

 .assembly-preview {
   display: flex;
   flex-direction: column;
   align-items: center;
   width: 100%;
   max-width: 800px;
 }

 .assembly-parts {
   display: flex;
   gap: 0;
   margin-bottom: 20px;
   border: 3px solid #28a745;
   border-radius: 15px;
   overflow: hidden;
   box-shadow: 0 10px 30px rgba(40, 167, 69, 0.3);
   background: #fff;
 }

 .assembly-part {
   background: linear-gradient(145deg, #f8f9fa, #e9ecef);
   border: none;
   border-radius: 0;
   cursor: pointer;
   transition: all 0.3s ease;
   position: relative;
   overflow: hidden;
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   border-right: 2px solid #dee2e6;
 }

 .assembly-part:hover {
   background: linear-gradient(145deg, #e9ecef, #dee2e6);
   transform: scale(1.02);
 }

.assembly-part.active {
  background: linear-gradient(145deg, #28a745, #1e7e34);
  color: white;
}

.assembly-part.highlighted {
  background: linear-gradient(145deg, #e3f2fd, #bbdefb);
}

.assembly-part:last-child {
  border-right: none;
}

.assembly-part.head-part {
  width: 180px;
  height: 180px;
}

.assembly-part.body-part {
  width: 320px;
  height: 180px;
}

.assembly-part.tail-part {
  width: 180px;
  height: 180px;
}

 .assembly-placeholder {
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   color: #6c757d;
   font-size: 0.85rem;
   text-align: center;
   height: 100%;
   width: 100%;
 }
 
 .assembly-placeholder i {
   font-size: 2.5rem;
   margin-bottom: 8px;
   color: #dee2e6;
 }
 
 .assembly-part.active .assembly-placeholder {
   display: none;
 }
 
 .assembly-image {
   width: 100%;
   height: 100%;
   object-fit: cover;
   border-radius: 0;
   transition: all 0.3s ease;
 }

 .assembly-part.head-part .assembly-image {
   object-position: right center;
 }

 .assembly-part.body-part .assembly-image {
   object-position: center center;
 }

 .assembly-part.tail-part .assembly-image {
   object-position: left center;
 }

.assembly-actions {
  margin-top: 20px;
  display: flex;
  gap: 10px;
}

.assembly-btn {
  color: white;
  border: 2px solid transparent;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  position: relative;
  overflow: hidden;
}

.assembly-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
  border-color: rgba(255,255,255,0.3);
}

.assembly-btn.reset-btn {
  background: linear-gradient(145deg, #e74c3c, #c82333);
}

 .assembly-btn.complete-btn {
   background: linear-gradient(145deg, #28a745, #1e7e34);
   position: relative;
   overflow: hidden;
 }

 .assembly-btn.complete-btn::before {
   content: '';
   position: absolute;
   top: 0;
   left: -100%;
   width: 100%;
   height: 100%;
   background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
   transition: left 0.5s;
 }

 .assembly-btn.complete-btn:hover::before {
   left: 100%;
 }

.assembly-btn.reset-btn:hover {
  background: linear-gradient(145deg, #c82333, #a31f27);
}

 .assembly-btn.complete-btn:hover {
   background: linear-gradient(145deg, #1e7e34, #145a26);
 }

 .assembly-btn:disabled {
   background: #6c757d !important;
   cursor: not-allowed;
   opacity: 0.6;
 }

 .assembly-btn:disabled:hover {
   transform: none;
   box-shadow: none;
 }



/* 设计工具模块 */
.design-tools {
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 2px solid #007bff;
}

.design-tools h2 {
  color: #007bff;
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 30px;
  font-weight: bold;
}

.design-workspace {
  display: grid;
  grid-template-columns: 200px 1fr 200px;
  gap: 30px;
  align-items: start;
}

.tool-panel {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #dee2e6;
}

.tool-panel h3 {
  color: #495057;
  margin-bottom: 20px;
  font-size: 1.2rem;
  text-align: center;
  font-weight: bold;
}

/* 颜色选择按钮样式 */
.color-buttons {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.color-btn {
  color: white;
  border: 2px solid transparent;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  position: relative;
  overflow: hidden;
}

.color-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
  border-color: rgba(255,255,255,0.3);
}

.color-btn.active {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 25px rgba(0,0,0,0.4);
  border-color: rgba(255,255,255,0.6);
}

.color-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.color-btn:hover::before {
  left: 100%;
}

.design-canvas {
  background: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 15px;
  padding: 30px;
  text-align: center;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
}

.design-canvas.active {
  border-color: #28a745;
  background: #f8fff9;
}

.canvas-image {
  max-width: 100%;
  max-height: 450px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.canvas-placeholder {
  color: #6c757d;
  text-align: center;
}

.canvas-placeholder-icon {
  font-size: 5rem;
  color: #dee2e6;
  margin-bottom: 20px;
}

/* 拼接完成后的展示样式 */
.assembled-dragon-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f8fff9, #e8f5e8);
  border-radius: 15px;
  border: 2px solid #28a745;
  animation: fadeInScale 0.8s ease-out;
}

.assembled-dragon-header {
  text-align: center;
  margin-bottom: 20px;
}

.assembled-dragon-header h3 {
  color: #28a745;
  font-size: 1.8rem;
  margin-bottom: 8px;
  font-weight: bold;
}

.assembled-dragon-header p {
  color: #6c757d;
  font-size: 1rem;
  margin: 0;
}

.assembled-dragon-parts {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 20px;
  border: 2px solid #28a745;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
  background: #fff;
}

.assembled-part {
  height: 150px;
  object-fit: cover;
  transition: all 0.3s ease;
}

.assembled-part:hover {
  transform: scale(1.05);
  z-index: 2;
  position: relative;
}

.head-display {
  width: 120px;
  object-position: right center;
}

.body-display {
  width: 200px;
  object-position: center center;
}

.tail-display {
  width: 120px;
  object-position: left center;
}

.assembled-dragon-info {
  width: 100%;
  max-width: 400px;
}

.assembly-stats {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 10px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  flex: 1;
  text-align: center;
}

.stat-item i {
  font-size: 1.5rem;
  color: #28a745;
}

.stat-item span {
  font-size: 0.85rem;
  color: #495057;
  font-weight: 500;
}

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 属性面板 */
.property-panel {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #dee2e6;
}

.property-group {
  margin-bottom: 20px;
}

.property-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #495057;
}

.property-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  font-size: 0.9rem;
}

.number-input {
  width: 80px;
  margin-left: 10px;
  display: inline-block;
}

.property-group span {
  margin-left: 10px;
  color: #6c757d;
  font-size: 0.9rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .lab-workspace {
    flex-direction: column;
    gap: 20px;
  }

  .dragon-parts-sidebar {
    flex: none;
  }

  .parts-display {
    flex: none;
    min-height: 300px;
  }

  .parts-images-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .assembly-workspace {
    gap: 15px;
  }

  .assembly-parts {
    flex-direction: column;
    gap: 0;
  }

  .assembly-part {
    width: 100% !important;
    height: 150px !important;
    border-right: none;
    border-bottom: 2px solid #dee2e6;
  }

  .assembly-part:last-child {
    border-bottom: none;
  }

  .design-workspace {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .assembled-dragon-parts {
    flex-direction: column;
    gap: 0;
  }

  .assembled-part {
    width: 100% !important;
    height: 120px;
  }

  .assembly-stats {
    flex-direction: column;
    gap: 8px;
  }

  .stat-item {
    flex-direction: row;
    justify-content: center;
    gap: 10px;
  }

  .page-header h1 {
    font-size: 2rem;
  }

  .virtual-lab h2,
  .design-tools h2,
  .dragon-assembly h2 {
    font-size: 2rem;
  }
}

@media (max-width: 480px) {
  .dragon-boat-container {
    padding: 10px;
  }
  
  .page-header {
    padding: 40px 15px;
  }
  
  .virtual-lab,
  .design-tools {
    padding: 20px;
  }
  
  .dragon-part {
    padding: 15px;
    gap: 12px;
  }
  
  .dragon-part-icon {
    font-size: 2rem;
    width: 50px;
    height: 50px;
  }
  
  .dragon-part h3 {
    font-size: 1.1rem;
  }
  
  .dragon-part p {
    font-size: 0.8rem;
  }

  .parts-images-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .parts-image-small {
    height: 150px;
  }

  .assembly-workspace {
    gap: 10px;
  }

  .assembly-parts {
    flex-direction: column;
    gap: 0;
  }

  .assembly-part {
    width: 100% !important;
    height: 120px !important;
    padding: 15px;
    border-right: none;
    border-bottom: 2px solid #dee2e6;
  }

  .assembly-part:last-child {
    border-bottom: none;
  }

  .assembly-placeholder i {
    font-size: 2rem;
    margin-bottom: 5px;
  }

  .assembly-placeholder {
    font-size: 0.8rem;
  }

  .assembly-actions {
    flex-direction: column;
    gap: 10px;
  }

  .assembly-btn {
    width: 100%;
    justify-content: center;
  }

  .assembled-dragon-display {
    padding: 15px;
  }

  .assembled-dragon-header h3 {
    font-size: 1.5rem;
  }

  .assembled-dragon-header p {
    font-size: 0.9rem;
  }

  .assembled-dragon-parts {
    margin-bottom: 15px;
  }

  .assembled-part {
    height: 100px !important;
  }

  .stat-item {
    padding: 8px;
    font-size: 0.8rem;
  }

  .stat-item i {
    font-size: 1.2rem;
  }
}
</style> 